import React, {FC, useCallback, useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap-v5'
import {ToastContainer} from 'react-toastify'
import {PageTitle} from '../../../../_metronic/layout/core'
import {ConfirmModal} from '../../../../shared/components/ConfirmModal'
import DataTable, {TableAction, TableColumn} from '../../../../shared/components/DataTable'
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import {apiErrorMessage} from '../../public/PublicModels'
import {SearchFilterBar} from '../../hr/shared/SearchFilterBar'
import {ICorporateModel} from '../corporate/CorporateModel'
import {
  assignCorporateToUser,
  loadCorporateOptions,
  removeCorporateFromUser,
  searchUsersWithCorporates,
} from './Api'
import {IUserCorporateMembership, IUserCorporatesModel} from './UserCorporateModel'

const userName = (user: IUserCorporatesModel) =>
  [user.firstName, user.lastName].filter(Boolean).join(' ') || user.username

/**
 * Admin: kullanıcıyı firmaya bağlama. Kullanıcı oturum açtığında varsayılan firması
 * (yoksa ilk firması) ile çalışır; hiçbir firmaya bağlı değilse İK ekranlarını kullanamaz.
 */
const UserCorporatePage: FC = () => {
  const [query, setQuery] = useState('')
  const [users, setUsers] = useState<IUserCorporatesModel[]>([])
  const [corporates, setCorporates] = useState<ICorporateModel[]>([])
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [loading, setLoading] = useState(false)
  const [assigningUser, setAssigningUser] = useState<IUserCorporatesModel | null>(null)
  const [selectedCorporateId, setSelectedCorporateId] = useState('')
  const [makeDefault, setMakeDefault] = useState(true)
  const [saving, setSaving] = useState(false)
  const [removing, setRemoving] = useState<{user: IUserCorporatesModel; membership: IUserCorporateMembership} | null>(
    null
  )

  const fetchUsers = useCallback(async (page: number, q: string) => {
    setLoading(true)
    try {
      const payload = (await searchUsersWithCorporates(q, page)).data.payload
      setUsers(payload.content.map((user) => ({...user, id: user.userId})))
      setTotalPages(payload.totalPages)
      setCurrentPage(page)
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Kullanıcılar yüklenemedi'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchUsers(0, query)
  }, [query, fetchUsers])

  useEffect(() => {
    loadCorporateOptions()
      .then(({data}) => setCorporates(data.payload))
      .catch(() => showErrorToast('Firmalar yüklenemedi'))
  }, [])

  const openAssign = (user: IUserCorporatesModel) => {
    setSelectedCorporateId('')
    setMakeDefault(user.corporates.length === 0)
    setAssigningUser(user)
  }

  const handleAssign = async () => {
    if (!assigningUser || !selectedCorporateId) return
    setSaving(true)
    try {
      await assignCorporateToUser(assigningUser.userId, selectedCorporateId, makeDefault)
      showSuccessToast('Kullanıcı firmaya bağlandı')
      setAssigningUser(null)
      await fetchUsers(currentPage, query)
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Bağlanamadı'))
    } finally {
      setSaving(false)
    }
  }

  const handleMakeDefault = async (user: IUserCorporatesModel, membership: IUserCorporateMembership) => {
    try {
      await assignCorporateToUser(user.userId, membership.corporateId, true)
      showSuccessToast(`${membership.corporateName} varsayılan firma yapıldı`)
      await fetchUsers(currentPage, query)
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Güncellenemedi'))
    }
  }

  const handleRemove = async () => {
    if (!removing) return
    try {
      await removeCorporateFromUser(removing.user.userId, removing.membership.corporateId)
      showSuccessToast('Bağlantı kaldırıldı')
      await fetchUsers(currentPage, query)
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Kaldırılamadı'))
    } finally {
      setRemoving(null)
    }
  }

  const columns: TableColumn<IUserCorporatesModel>[] = [
    {
      key: 'username',
      label: 'Kullanıcı',
      render: (_, user) => (
        <div className='d-flex flex-column'>
          <span className='fw-bolder text-gray-800'>{userName(user)}</span>
          <span className='text-muted fs-7'>{user.email}</span>
          {!user.active && <span className='text-warning fs-8'>Hesap aktif değil</span>}
        </div>
      ),
    },
    {
      key: 'corporates',
      label: 'Firmalar',
      render: (_, user) =>
        user.corporates.length === 0 ? (
          <span className='text-danger fs-7'>Hiçbir firmaya bağlı değil</span>
        ) : (
          <div className='d-flex flex-wrap gap-2'>
            {user.corporates.map((membership) => (
              <span
                key={membership.corporateId}
                className={`badge ${membership.defaultCorporate ? 'badge-light-primary' : 'badge-light'} d-inline-flex align-items-center gap-2 py-2 px-3`}
                title={membership.defaultCorporate ? 'Varsayılan firma' : ''}
              >
                {membership.defaultCorporate && <i className='bi bi-star-fill text-primary fs-8'></i>}
                {membership.corporateName}
                {!membership.corporateActive && <span className='text-muted'>(pasif)</span>}
                {!membership.defaultCorporate && (
                  <button
                    type='button'
                    className='btn btn-icon btn-sm h-auto w-auto p-0'
                    title='Varsayılan yap'
                    onClick={() => handleMakeDefault(user, membership)}
                  >
                    <i className='bi bi-star fs-8'></i>
                  </button>
                )}
                <button
                  type='button'
                  className='btn btn-icon btn-sm h-auto w-auto p-0'
                  title='Bağlantıyı kaldır'
                  onClick={() => setRemoving({user, membership})}
                >
                  <i className='bi bi-x-lg fs-8 text-danger'></i>
                </button>
              </span>
            ))}
          </div>
        ),
    },
  ]

  const actions: TableAction<IUserCorporatesModel>[] = [
    {label: 'Firmaya Bağla', icon: 'bi bi-link-45deg', className: 'btn-light-primary', onClick: openAssign},
  ]

  const assignableCorporates = corporates.filter(
    (corporate) => !assigningUser?.corporates.some((membership) => membership.corporateId === corporate.id)
  )

  return (
    <>
      <PageTitle breadcrumbs={[]}>Kullanıcı - Firma</PageTitle>

      <div className='card'>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bolder fs-3 mb-1'>Kullanıcı - Firma Bağlantıları</span>
            <span className='text-muted mt-1 fw-bold fs-7'>
              Kullanıcı oturum açtığında yıldızlı (varsayılan) firmasıyla çalışır
            </span>
          </h3>
        </div>
        <div className='card-body pt-0'>
          <SearchFilterBar
            placeholder='Kullanıcı adı, ad soyad veya e-posta ile ara'
            onSearch={setQuery}
            onFilter={() => undefined}
          />
          <DataTable
            data={users}
            columns={columns}
            actions={actions}
            showPagination={true}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => page >= 0 && page < totalPages && fetchUsers(page, query)}
            loading={loading}
            emptyMessage='Kullanıcı bulunamadı'
          />
        </div>
      </div>

      <Modal show={!!assigningUser} onHide={() => setAssigningUser(null)} dialogClassName='modal-dialog-centered'>
        <div className='modal-header'>
          <h5 className='modal-title'>{assigningUser && userName(assigningUser)} · Firmaya Bağla</h5>
          <button type='button' className='btn-close' onClick={() => setAssigningUser(null)} />
        </div>
        <div className='modal-body'>
          <label className='form-label fw-bold required'>Firma</label>
          <select
            className='form-select mb-5'
            value={selectedCorporateId}
            onChange={(event) => setSelectedCorporateId(event.target.value)}
          >
            <option value=''>Seçiniz</option>
            {assignableCorporates.map((corporate) => (
              <option key={corporate.id || ''} value={corporate.id || ''}>
                {corporate.name}
                {corporate.active ? '' : ' (pasif)'}
              </option>
            ))}
          </select>
          <div className='form-check form-check-custom form-check-solid'>
            <input
              id='make-default'
              type='checkbox'
              className='form-check-input'
              checked={makeDefault}
              onChange={(event) => setMakeDefault(event.target.checked)}
            />
            <label className='form-check-label' htmlFor='make-default'>
              Varsayılan firması olsun
            </label>
          </div>
        </div>
        <div className='modal-footer'>
          <button type='button' className='btn btn-light' onClick={() => setAssigningUser(null)}>
            İptal
          </button>
          <button
            type='button'
            className='btn btn-primary'
            disabled={!selectedCorporateId || saving}
            onClick={handleAssign}
          >
            {saving ? <span className='spinner-border spinner-border-sm' /> : 'Bağla'}
          </button>
        </div>
      </Modal>

      <ConfirmModal
        show={!!removing}
        header='Bağlantıyı Kaldır'
        text={`${removing ? userName(removing.user) : ''} kullanıcısının "${removing?.membership.corporateName ?? ''}" firmasıyla bağlantısı kaldırılacak. Devam edilsin mi?`}
        handleClose={() => setRemoving(null)}
        applyOnClick={handleRemove}
      />

      <ToastContainer position='top-right' autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover />
    </>
  )
}

export {UserCorporatePage}
